import { formatTRY } from "@/lib/currency";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect, type ComponentType } from "react";
import {
  Search,
  Star,
  Globe,
  ChevronDown,
  ChevronRight,
  Check,
  ArrowLeft,
  X,
  Sparkles,
  Phone,
  MessageSquare,
  MapPin,
  Clock,
  Flame,
} from "lucide-react";

import logo from "@/assets/logo.png";

// Lucide bu sürümde marka ikonlarını kaldırdığı için özel SVG'ler
const Instagram = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);
const Facebook = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 8H6v4h3v12h5V12h3.642L18 8h-4V6.333C14 5.378 14.192 5 15.115 5H18V0h-3.808C10.596 0 9 1.583 9 4.615V8z" />
  </svg>
);
import { categories, products, getProductImage, type Product } from "@/components/dashboard/data";
import { DEFAULT_QR_THEME, getQrFontStack, ensureQrFontLoaded } from "@/lib/qr-menu-theme";

export const Route = createFileRoute("/menu")({
  component: MenuRouteComponent,
});

type LangType = "tr" | "en";

// Translation dictionary for premium UI
const translations: Record<LangType, Record<string, string>> = {
  tr: {
    title: "Seçkin QR Menü Deneyimi",
    subtitle: "Sanatsal Lezzetler & Özel Tarifler",
    searchPlaceholder: "En sevdiğiniz lezzeti arayın...",
    favorites: "Şefin Favorileri",
    noProducts: "Arayışınıza Uygun Lezzet Bulunamadı",
    noProductsSub: "Farklı bir arama yapmayı deneyebilir veya kategorilere göz atabilirsiniz.",
    choice: "Şefin Özel Seçimi",
    coffee: "Nitelikli Kahveler",
    tea: "Gurme Bitki Çayları",
    "cold-drinks": "El Yapımı Soğuk İçecekler",
    bakery: "Günlük Fırın Ürünleri",
    desserts: "Sanatsal Tatlılar",
    sandwiches: "Gurme Sandviçler",
    pizza: "Taş Fırın Pizzaları",
    pasta: "Ev Yapımı Makarnalar",
    soups: "Geleneksel Çorbalar",
    salads: "Taze Salata Kasesi",
    smoothies: "Doğal Smoothie",
    sides: "Leziz Yan Ürünler",
    snacks: "Atıştırmalıklar",
    "ice-cream": "Artizan Dondurma",
    breakfast: "Zengin Kahvaltı",
    kids: "Çocuk Menüsü",
    "beer-wine": "Bira & Butik Şaraplar",
    cocktails: "İmza Kokteyller",
    merch: "Özel Koleksiyon",
    back: "Geri",
    items: "Özel Tarif",
    allCategories: "Menü Kategorileri",
    searchResults: "Arama Sonuçları",
    popular: "En Çok Tercih Edilen",
    statusOpen: "AÇIK • MASA SİPARİŞİ AKTİF",
    close: "Kapat",
    price: "Fiyat",
    chefFavorite: "Şefin Favorisi",
    promotions: "Kampanyalar & Duyurular",
    contactTitle: "İletişim & Konum",
    call: "Ara",
    whatsapp: "WhatsApp",
    instagram: "Instagram",
    facebook: "Facebook",
    website: "Web Sitesi",
    directions: "Yol Tarifi",
    hours: "Çalışma Saatleri",
    outOfStock: "Tükendi",
    allergensLabel: "Alerjenler",
    caloriesLabel: "Kalori",
    "catdesc-favorites": "Misafirlerimizin en çok sevdiği, şefimizin öne çıkardığı imza lezzetler.",
    "catdesc-coffee":
      "Özenle kavrulmuş çekirdeklerden, baristalarımızın elinden nitelikli kahveler.",
    "catdesc-tea": "Dinlendiren ve tazeleyen, özenle harmanlanmış bitki ve yaprak çayları.",
    "catdesc-cold-drinks": "Günün her saati serinleten el yapımı soğuk içecekler.",
    "catdesc-bakery": "Her sabah taze pişen fırın ürünlerimizden günlük seçkiler.",
    "catdesc-desserts": "Şefimizin imzasını taşıyan el yapımı sanatsal tatlılar.",
    "catdesc-sandwiches": "Taze malzemelerle hazırlanan doyurucu gurme sandviçler.",
    "catdesc-pizza": "Taş fırında odun ateşiyle pişen incecik hamurlu pizzalar.",
    "catdesc-pasta": "Ev yapımı taze makarnalar, klasik İtalyan sosları ile.",
    "catdesc-soups": "Geleneksel tariflerle hazırlanan günün sıcak çorbaları.",
    "catdesc-salads": "Mevsim yeşillikleriyle hazırlanan taptaze salata kaseleri.",
    "catdesc-smoothies": "Taze meyve ve sebzelerden şekersiz doğal karışımlar.",
    "catdesc-sides": "Ana lezzetlerinize eşlik edecek leziz yan ürünler.",
    "catdesc-snacks": "Hafif atıştırmalıklar ve pratik lezzetler.",
    "catdesc-ice-cream": "Günlük üretim artizan dondurmalar, doğal aromalarla.",
    "catdesc-breakfast": "Güne enerjik başlamanızı sağlayan zengin kahvaltı seçenekleri.",
    "catdesc-kids": "Minik misafirlerimiz için özenle hazırlanan çocuk menüsü.",
    "catdesc-beer-wine": "Yerel biralar ve butik bağlardan seçilmiş şaraplar.",
    "catdesc-cocktails": "Miksologlarımızın imza kokteylleri ve zamansız klasikler.",
    "catdesc-merch": "Mekânımızın özel koleksiyon ürünleri ve hediyelikler.",
  },
  en: {
    title: "Signature QR Menu Experience",
    subtitle: "Artisanal Delights & Specially Crafted Recipes",
    searchPlaceholder: "Search for your favorite taste...",
    favorites: "Chef's Favorites",
    noProducts: "No Delights Match Your Search",
    noProductsSub: "You can try another term or browse our categories.",
    choice: "Chef's Special Selection",
    coffee: "Specialty Coffees",
    tea: "Gourmet Herbal Teas",
    "cold-drinks": "Artisanal Cold Drinks",
    bakery: "Freshly Baked Goods",
    desserts: "Artistic Pastries",
    sandwiches: "Gourmet Sandwiches",
    pizza: "Stone Oven Pizzas",
    pasta: "Artisanal Pastas",
    soups: "Traditional Soups",
    salads: "Fresh Salad Bowls",
    smoothies: "Natural Smoothies",
    sides: "Delicious Sides",
    snacks: "Bites & Snacks",
    "ice-cream": "Artisanal Ice Cream",
    breakfast: "Rich Breakfast",
    kids: "Kids Menu",
    "beer-wine": "Beers & Boutique Wines",
    cocktails: "Signature Cocktails",
    merch: "Special Collection",
    back: "Back",
    items: "Recipes",
    allCategories: "Menu Categories",
    searchResults: "Search Results",
    popular: "Highly Demanded",
    close: "Close",
    price: "Price",
    chefFavorite: "Chef's Favorite",
    promotions: "Promotions & Announcements",
    contactTitle: "Contact & Location",
    call: "Call",
    whatsapp: "WhatsApp",
    instagram: "Instagram",
    facebook: "Facebook",
    website: "Website",
    directions: "Directions",
    hours: "Working Hours",
    outOfStock: "Sold Out",
    allergensLabel: "Allergens",
    caloriesLabel: "Calories",
    "catdesc-favorites": "Our guests' most beloved signature delights, curated by our chef.",
    "catdesc-coffee": "Specialty coffees from carefully roasted beans, crafted by our baristas.",
    "catdesc-tea": "Soothing and refreshing, carefully blended herbal and leaf teas.",
    "catdesc-cold-drinks": "Handcrafted cold drinks to cool you down any time of day.",
    "catdesc-bakery": "Daily selections from our bakery, baked fresh every morning.",
    "catdesc-desserts": "Handmade artistic desserts bearing our chef's signature.",
    "catdesc-sandwiches": "Hearty gourmet sandwiches prepared with fresh ingredients.",
    "catdesc-pizza": "Thin-crust pizzas baked over a wood fire in our stone oven.",
    "catdesc-pasta": "Fresh homemade pastas with classic Italian sauces.",
    "catdesc-soups": "Warm soups of the day, prepared with traditional recipes.",
    "catdesc-salads": "Fresh salad bowls prepared with seasonal greens.",
    "catdesc-smoothies": "Natural sugar-free blends of fresh fruits and vegetables.",
    "catdesc-sides": "Delicious sides to accompany your main delights.",
    "catdesc-snacks": "Light bites and practical treats.",
    "catdesc-ice-cream": "Artisanal ice cream made daily with natural flavors.",
    "catdesc-breakfast": "Rich breakfast options for an energetic start to your day.",
    "catdesc-kids": "A kids menu carefully prepared for our little guests.",
    "catdesc-beer-wine": "Local beers and wines selected from boutique vineyards.",
    "catdesc-cocktails": "Signature cocktails by our mixologists and timeless classics.",
    "catdesc-merch": "Our venue's special collection items and gifts.",
  },
};

// Design Configuration mapping category IDs to dynamic visual presets
const CATEGORY_STYLES: Record<string, { bg: string; text: string; glow: string; badge: string }> = {
  favorites: {
    bg: "bg-amber-50",
    text: "text-amber-600",
    glow: "shadow-[0_20px_50px_rgba(245,158,11,0.06)] hover:shadow-amber-500/10 hover:border-amber-500/30",
    badge: "bg-amber-100/70 text-amber-800",
  },
  coffee: {
    bg: "bg-amber-100/50",
    text: "text-amber-800",
    glow: "shadow-[0_20px_50px_rgba(120,53,4,0.04)] hover:shadow-amber-900/10 hover:border-amber-800/30",
    badge: "bg-amber-100 text-amber-900",
  },
  tea: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    glow: "shadow-[0_20px_50px_rgba(16,185,129,0.04)] hover:shadow-emerald-500/10 hover:border-emerald-500/30",
    badge: "bg-emerald-100 text-emerald-800",
  },
  "cold-drinks": {
    bg: "bg-sky-50",
    text: "text-sky-700",
    glow: "shadow-[0_20px_50px_rgba(14,165,233,0.04)] hover:shadow-sky-500/10 hover:border-sky-500/30",
    badge: "bg-sky-100 text-sky-800",
  },
  bakery: {
    bg: "bg-orange-50",
    text: "text-orange-700",
    glow: "shadow-[0_20px_50px_rgba(249,115,22,0.04)] hover:shadow-orange-500/10 hover:border-orange-500/30",
    badge: "bg-orange-100 text-orange-850",
  },
  desserts: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    glow: "shadow-[0_20px_50px_rgba(244,63,94,0.04)] hover:shadow-rose-500/10 hover:border-rose-500/30",
    badge: "bg-rose-100 text-rose-800",
  },
  sandwiches: {
    bg: "bg-yellow-50",
    text: "text-yellow-800",
    glow: "shadow-[0_20px_50px_rgba(234,179,8,0.04)] hover:shadow-yellow-500/10 hover:border-yellow-500/30",
    badge: "bg-yellow-100 text-yellow-805",
  },
  pizza: {
    bg: "bg-red-50",
    text: "text-red-700",
    glow: "shadow-[0_20px_50px_rgba(239,68,68,0.04)] hover:shadow-red-500/10 hover:border-red-500/30",
    badge: "bg-red-100 text-red-800",
  },
  pasta: {
    bg: "bg-amber-50/70",
    text: "text-amber-800",
    glow: "shadow-[0_20px_50px_rgba(217,119,6,0.04)] hover:shadow-amber-500/10 hover:border-amber-500/30",
    badge: "bg-amber-100 text-amber-850",
  },
  soups: {
    bg: "bg-stone-100/80",
    text: "text-stone-700",
    glow: "shadow-[0_20px_50px_rgba(120,113,108,0.04)] hover:shadow-stone-500/10 hover:border-stone-500/30",
    badge: "bg-stone-200 text-stone-800",
  },
  salads: {
    bg: "bg-green-50",
    text: "text-green-700",
    glow: "shadow-[0_20px_50px_rgba(34,197,94,0.04)] hover:shadow-green-500/10 hover:border-green-500/30",
    badge: "bg-green-100 text-green-800",
  },
  smoothies: {
    bg: "bg-purple-50",
    text: "text-purple-700",
    glow: "shadow-[0_20px_50px_rgba(168,85,247,0.04)] hover:shadow-purple-500/10 hover:border-purple-500/30",
    badge: "bg-purple-100 text-purple-800",
  },
  sides: {
    bg: "bg-neutral-50",
    text: "text-neutral-700",
    glow: "shadow-[0_20px_50px_rgba(115,115,115,0.04)] hover:shadow-neutral-500/10 hover:border-neutral-500/30",
    badge: "bg-neutral-100 text-neutral-800",
  },
  snacks: {
    bg: "bg-yellow-50/60",
    text: "text-yellow-900",
    glow: "shadow-[0_20px_50px_rgba(234,179,8,0.04)] hover:shadow-yellow-500/10 hover:border-yellow-500/30",
    badge: "bg-yellow-100/80 text-yellow-900",
  },
  "ice-cream": {
    bg: "bg-pink-50",
    text: "text-pink-700",
    glow: "shadow-[0_20px_50px_rgba(236,72,153,0.04)] hover:shadow-pink-500/10 hover:border-pink-500/30",
    badge: "bg-pink-100 text-pink-700",
  },
  breakfast: {
    bg: "bg-amber-50/50",
    text: "text-amber-700",
    glow: "shadow-[0_20px_50px_rgba(245,158,11,0.04)] hover:shadow-amber-500/10 hover:border-amber-500/30",
    badge: "bg-amber-100 text-amber-800",
  },
  kids: {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    glow: "shadow-[0_20px_50px_rgba(99,102,241,0.04)] hover:shadow-indigo-500/10 hover:border-indigo-500/30",
    badge: "bg-indigo-100 text-indigo-800",
  },
  "beer-wine": {
    bg: "bg-red-50/80",
    text: "text-red-900",
    glow: "shadow-[0_20px_50px_rgba(220,38,38,0.04)] hover:shadow-red-600/10 hover:border-red-600/30",
    badge: "bg-red-100 text-red-950",
  },
  cocktails: {
    bg: "bg-teal-50",
    text: "text-teal-700",
    glow: "shadow-[0_20px_50px_rgba(20,184,166,0.04)] hover:shadow-teal-500/10 hover:border-teal-500/30",
    badge: "bg-teal-100 text-teal-800",
  },
  merch: {
    bg: "bg-zinc-100/50",
    text: "text-zinc-800",
    glow: "shadow-[0_20px_50px_rgba(39,39,42,0.04)] hover:shadow-zinc-700/10 hover:border-zinc-700/30",
    badge: "bg-zinc-200 text-zinc-800",
  },
};

const DEFAULT_COVER_IMAGE =
  "https://images.unsplash.com/photo-1498804103079-a6351b050096?q=80&w=600&auto=format&fit=crop";

interface QrBanner {
  id: string;
  title: string;
  image: string;
  dateRange: string;
  visible: boolean;
  priority: number;
}

// QR Menü Yönetimi panelinde kaydedilen ve bu sayfada karşılığı olan ayarlar
interface QrMenuSettings {
  settingsVersion?: number;
  isEnabled?: boolean;
  logoUrl?: string;
  coverImageUrl?: string;
  businessName?: string;
  businessDescription?: string;
  showSearchBar?: boolean;
  showCategoryIcons?: boolean;
  showFeaturedProducts?: boolean;
  stickyCategories?: boolean;
  enableAnimations?: boolean;
  hidePrices?: boolean;
  hideUnavailable?: boolean;
  showAllergens?: boolean;
  showCalories?: boolean;
  isMultiLang?: boolean;
  defaultLanguage?: string;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  fontFamily?: string;
  borderRadius?: number;
  buttonStyle?: "sharp" | "rounded" | "pill";
  cardStyle?: "flat" | "elevated" | "glass";
  // İşletme / iletişim bilgileri
  businessPhone?: string;
  businessWhatsApp?: string;
  businessAddress?: string;
  businessGmaps?: string;
  businessInstagram?: string;
  businessFacebook?: string;
  businessWebsite?: string;
  workingHours?: string;
  // Tanıtım afişleri & SEO
  banners?: QrBanner[];
  seoTitle?: string;
  seoDescription?: string;
}

// Hex rengi verilen opaklıkla rgba'ya çevirir (soft rozet/çip zeminleri için)
function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.replace("#", "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return `rgba(217, 119, 6, ${alpha})`;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function loadQrMenuSettings(): QrMenuSettings {
  if (typeof window === "undefined") return {};
  try {
    const saved = window.localStorage.getItem("qr_menu_settings");
    if (!saved) return {};
    const parsed = JSON.parse(saved) as QrMenuSettings;
    // Renk desteğinden önceki kayıtların renk alanlarını yok say (varsayılan beyaz palet kullanılır)
    if (typeof parsed.settingsVersion !== "number" || parsed.settingsVersion < 2) {
      delete parsed.primaryColor;
      delete parsed.secondaryColor;
      delete parsed.backgroundColor;
    }
    return parsed;
  } catch {
    return {};
  }
}

function MenuRouteComponent() {
  const [settings] = useState<QrMenuSettings>(loadQrMenuSettings);
  const [lang, setLang] = useState<LangType>(settings.defaultLanguage === "en" ? "en" : "tr");
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Admin ayarlarından türetilen görünüm değerleri.
  // Görünüm/davranış anahtarları: kaydedilmişse o değer, yoksa aktif tema şablonu.
  const isEnabled = settings.isEnabled !== false;
  const isMultiLang = settings.isMultiLang !== false;
  const showSearchBar = settings.showSearchBar ?? DEFAULT_QR_THEME.showSearchBar;
  const showCategoryIcons = settings.showCategoryIcons ?? DEFAULT_QR_THEME.showCategoryIcons;
  const showFavorites = settings.showFeaturedProducts ?? DEFAULT_QR_THEME.showFeaturedProducts;
  const animationsEnabled = settings.enableAnimations ?? DEFAULT_QR_THEME.enableAnimations;
  const hidePrices = settings.hidePrices ?? DEFAULT_QR_THEME.hidePrices;
  const hideUnavailable = settings.hideUnavailable ?? DEFAULT_QR_THEME.hideUnavailable;
  const showAllergens = settings.showAllergens ?? DEFAULT_QR_THEME.showAllergens;
  const showCalories = settings.showCalories ?? DEFAULT_QR_THEME.showCalories;
  const businessName = settings.businessName?.trim() || "VIDYON";
  const logoSrc = settings.logoUrl || logo;
  const coverSrc = settings.coverImageUrl || DEFAULT_COVER_IMAGE;

  // İşletme / iletişim bilgileri (boşsa gösterilmez)
  const businessPhone = settings.businessPhone?.trim() || "";
  const businessWhatsApp = settings.businessWhatsApp?.trim() || "";
  const businessAddress = settings.businessAddress?.trim() || "";
  const businessGmaps = settings.businessGmaps?.trim() || "";
  const businessInstagram = settings.businessInstagram?.trim() || "";
  const businessFacebook = settings.businessFacebook?.trim() || "";
  const businessWebsite = settings.businessWebsite?.trim() || "";
  const workingHours = settings.workingHours?.trim() || "";
  const hasContactInfo = !!(
    businessPhone ||
    businessWhatsApp ||
    businessInstagram ||
    businessFacebook ||
    businessWebsite ||
    businessGmaps ||
    businessAddress ||
    workingHours
  );

  // Aktif tanıtım afişleri (öncelik sırasına göre)
  const activeBanners = useMemo(
    () => (settings.banners || []).filter((b) => b.visible).sort((a, b) => a.priority - b.priority),
    [settings.banners],
  );

  // SEO başlığını sekmeye uygula
  useEffect(() => {
    const title = settings.seoTitle?.trim() || `${businessName} — QR Menü`;
    document.title = title;
  }, [settings.seoTitle, businessName]);

  // Renk ayarları — varsayılanlar her zaman aktif tema şablonundan gelir,
  // kullanıcı panelden kaydettiyse onun değerleri kullanılır.
  const accentColor = settings.primaryColor || DEFAULT_QR_THEME.primary;
  const inkColor = settings.secondaryColor || DEFAULT_QR_THEME.secondary;
  const pageBackground = settings.backgroundColor || DEFAULT_QR_THEME.background;
  const accentSoft = hexToRgba(accentColor, 0.1);

  // Biçimlendirme & Düzen ayarları (default'lar tema şablonundan)
  const fontFamily = settings.fontFamily || DEFAULT_QR_THEME.font;
  const radius =
    typeof settings.borderRadius === "number" ? settings.borderRadius : DEFAULT_QR_THEME.radius;
  const buttonStyle = settings.buttonStyle || DEFAULT_QR_THEME.buttonStyle;
  const cardStyle = settings.cardStyle || DEFAULT_QR_THEME.cardStyle;

  const fontStack = getQrFontStack(fontFamily);
  const rowRadius = Math.max(10, Math.round(radius * 0.85));
  const thumbRadius = Math.max(8, Math.round(radius * 0.6));
  const chipRadius = buttonStyle === "sharp" ? 6 : buttonStyle === "rounded" ? 14 : 999;
  // Kart yüzeyi: flat = gölgesiz beyaz, elevated = gölgeli beyaz, glass = buzlu cam
  const cardSurface =
    cardStyle === "glass"
      ? { backgroundColor: "rgba(255,255,255,0.6)", backdropFilter: "blur(12px)" }
      : { backgroundColor: "#FFFFFF" };
  const cardShadowClass =
    cardStyle === "elevated"
      ? "shadow-[0_4px_12px_rgba(0,0,0,0.015)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.03)]"
      : "";

  // Seçili yazı tipini (gerekiyorsa) Google Fonts'tan yükle
  useEffect(() => {
    ensureQrFontLoaded(fontFamily);
  }, [fontFamily]);

  const t = (key: string) => {
    return translations[lang][key] || key;
  };

  // Translate category labels dynamically
  const getCategoryLabel = (catId: string, defaultLabel: string) => {
    return translations[lang][catId] || defaultLabel;
  };

  // Translate product names dynamically
  const getProductName = (prod: Product) => {
    return translations[lang][prod.name] || prod.name;
  };

  // Ürünün kendi açıklaması yoksa kategori açıklamasına düşer
  const getProductDescription = (prod: Product) => {
    const own = lang === "en" ? prod.descriptionEn || prod.description : prod.description;
    return own || t(`catdesc-${prod.categoryId}`);
  };

  // Ürünün alerjenlerini aktif dile göre döndürür
  const getProductAllergens = (prod: Product) => {
    return (lang === "en" ? prod.allergensEn || prod.allergens : prod.allergens) || [];
  };

  // İletişim kanalları — sadece dolu olanlar; her biri tıklanabilir link
  const withProtocol = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);
  const contactChannels: {
    icon: ComponentType<{ className?: string }>;
    label: string;
    href: string;
  }[] = [];
  if (businessPhone)
    contactChannels.push({
      icon: Phone,
      label: t("call"),
      href: `tel:${businessPhone.replace(/\s/g, "")}`,
    });
  if (businessWhatsApp)
    contactChannels.push({
      icon: MessageSquare,
      label: t("whatsapp"),
      href: `https://wa.me/${businessWhatsApp.replace(/[^0-9]/g, "")}`,
    });
  if (businessInstagram)
    contactChannels.push({
      icon: Instagram,
      label: t("instagram"),
      href: businessInstagram.startsWith("http")
        ? businessInstagram
        : `https://instagram.com/${businessInstagram.replace(/^@/, "")}`,
    });
  if (businessFacebook)
    contactChannels.push({
      icon: Facebook,
      label: t("facebook"),
      href: businessFacebook.startsWith("http")
        ? businessFacebook
        : `https://facebook.com/${businessFacebook}`,
    });
  if (businessWebsite)
    contactChannels.push({ icon: Globe, label: t("website"), href: withProtocol(businessWebsite) });
  if (businessGmaps)
    contactChannels.push({ icon: MapPin, label: t("directions"), href: businessGmaps });

  // Filter products based on selected category OR search query
  const displayedProducts = useMemo(() => {
    // "Tükenenleri Gizle" ayarı açıksa stokta olmayanları listeden çıkar
    const inStock = (p: Product) => !(hideUnavailable && p.isAvailable === false);

    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      return products.filter((p) => {
        const trName = getProductName(p).toLowerCase();
        const origName = p.name.toLowerCase();
        return (trName.includes(q) || origName.includes(q)) && inStock(p);
      });
    }

    if (!selectedCategoryId) return [];

    if (selectedCategoryId === "favorites") {
      return products.filter((p) => p.isFavorite && inStock(p));
    }

    return products.filter((p) => p.categoryId === selectedCategoryId && inStock(p));
  }, [selectedCategoryId, searchQuery, lang, hideUnavailable]);

  const handleSelectLanguage = (selectedLang: LangType) => {
    setLang(selectedLang);
    setIsLangDropdownOpen(false);
  };

  // Calculate items count in categories
  const getProductCount = (catId: string) => {
    return products.filter((p) => p.categoryId === catId).length;
  };

  const getFavoritesCount = () => {
    return products.filter((p) => p.isFavorite).length;
  };

  // Find selected category label, icon and image for navigation header
  const selectedCategoryInfo = useMemo(() => {
    if (!selectedCategoryId) return null;
    const cat = categories.find((c) => c.id === selectedCategoryId);
    if (selectedCategoryId === "favorites") {
      return { label: t("favorites"), icon: Star, image: cat?.image };
    }
    return cat
      ? { label: getCategoryLabel(cat.id, cat.label), icon: cat.icon, image: cat.image }
      : null;
  }, [selectedCategoryId, lang]);

  // Kategori başlığı fotoğrafa tek satırda sığsın diye uzunluğa göre otomatik boyutlanır
  const categoryTitleSize = useMemo(() => {
    if (!selectedCategoryInfo) return 22;
    const len = selectedCategoryInfo.label.length;
    return Math.max(13, Math.min(26, Math.floor(340 / Math.max(len, 10))));
  }, [selectedCategoryInfo]);

  // Sheet için kategori etiketi
  const selectedProductCategoryLabel = useMemo(() => {
    if (!selectedProduct) return "";
    const cat = categories.find((c) => c.id === selectedProduct.categoryId);
    return cat ? getCategoryLabel(cat.id, cat.label) : "";
  }, [selectedProduct, lang]);

  // Kanal pasifse müşteriye kapalı ekranı göster
  if (!isEnabled) {
    return (
      <div className="min-h-screen w-full bg-[#f8f9fa] flex items-center justify-center font-sans antialiased px-6">
        <div className="max-w-sm w-full bg-white rounded-[32px] border border-zinc-100 shadow-[0_30px_100px_rgba(0,0,0,0.06)] p-10 flex flex-col items-center text-center">
          <div className="size-20 rounded-full bg-white border-[5px] border-zinc-100 flex items-center justify-center p-3 shadow-md mb-5">
            <img src={logoSrc} alt={businessName} className="w-full h-full object-contain" />
          </div>
          <h1 className="text-lg font-black tracking-[0.15em] text-zinc-950 uppercase">
            {businessName}
          </h1>
          <p className="text-sm font-bold text-zinc-700 mt-4">
            {lang === "tr" ? "Menümüz şu anda hizmet dışı." : "Our menu is currently unavailable."}
          </p>
          <p className="text-xs text-zinc-400 mt-1.5">
            {lang === "tr"
              ? "Kısa süre içinde tekrar yayında olacağız."
              : "We will be back online shortly."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full bg-[#f8f9fa] text-zinc-900 flex items-center justify-center antialiased overflow-hidden select-none"
      style={{ fontFamily: fontStack }}
    >
      {/* Decorative Blur Backdrops for Desktop frame */}
      <div
        className="absolute top-[10%] left-[20%] w-[350px] h-[350px] rounded-full blur-[120px] pointer-events-none hidden md:block"
        style={{ backgroundColor: hexToRgba(accentColor, 0.05) }}
      />
      <div className="absolute bottom-[10%] right-[20%] w-[350px] h-[350px] bg-rose-500/5 rounded-full blur-[120px] pointer-events-none hidden md:block" />

      {/* Desktop Container Frame (Vertically centered mobile layout) */}
      <div
        className="w-full max-w-[440px] h-screen sm:h-[880px] sm:max-h-[94vh] sm:rounded-[40px] sm:border-[6px] sm:border-white sm:shadow-[0_30px_100px_rgba(0,0,0,0.06),_0_0_1px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col relative transition-all duration-300"
        style={{ backgroundColor: pageBackground }}
      >
        {/* ========================================================
            NESTED FULL-SCREEN PRODUCTS VIEW (NEW PAGE SLIDE-IN EFFECT)
            ======================================================== */}
        {selectedCategoryId !== null && searchQuery.trim() === "" ? (
          <div
            className={`absolute inset-0 z-30 flex flex-col ${animationsEnabled ? "animate-in fade-in slide-in-from-right duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" : ""}`}
            style={{ backgroundColor: pageBackground }}
          >
            {/* Category Hero — büyük başlık fotoğrafın altına ortalanır, geri butonu ikon rozeti */}
            {selectedCategoryInfo && (
              <div className="relative h-44 w-full shrink-0 overflow-hidden">
                <img
                  src={selectedCategoryInfo.image}
                  alt={selectedCategoryInfo.label}
                  className="w-full h-full object-cover"
                />
                {/* Fotoğraf alta doğru zemine karışarak kaybolur (ana sayfa kapağı gibi) */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0) 40%, ${hexToRgba(pageBackground, 0.55)} 72%, ${pageBackground})`,
                  }}
                />

                {/* Geri butonu — sadece ikonlu rozet */}
                <button
                  onClick={() => setSelectedCategoryId(null)}
                  aria-label={t("back")}
                  className="absolute top-4 left-4 size-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-zinc-800 hover:text-zinc-950 shadow-md transition-all hover:scale-105 active:scale-95 cursor-pointer"
                >
                  <ArrowLeft className="size-4.5" />
                </button>

                {/* Kategori ikon rozeti */}
                {showCategoryIcons && (
                  <div
                    className={`absolute top-4 right-4 size-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md ${CATEGORY_STYLES[selectedCategoryId!]?.text || "text-zinc-800"}`}
                  >
                    <selectedCategoryInfo.icon className="size-4.5" />
                  </div>
                )}

                {/* Büyük başlık — alt ortada, tek satır, otomatik boyut */}
                <div className="absolute bottom-0 inset-x-0 px-5 pb-3 flex flex-col items-center text-center">
                  <h1
                    className="font-black uppercase tracking-[0.1em] leading-none whitespace-nowrap max-w-full overflow-hidden"
                    style={{ fontSize: categoryTitleSize, color: inkColor }}
                  >
                    {selectedCategoryInfo.label}
                  </h1>
                  <p className="text-[9px] text-zinc-500 font-semibold mt-1.5 line-clamp-1">
                    {t(`catdesc-${selectedCategoryId}`)}
                  </p>
                </div>
              </div>
            )}

            {/* Sub-page Products Content List */}
            <div className="flex-1 overflow-y-auto px-6 py-5 scroll-smooth">
              <div className="space-y-3.5 pb-12">
                {displayedProducts.map((product, idx) => (
                  <button
                    key={product.id}
                    onClick={() => setSelectedProduct(product)}
                    style={{
                      ...(animationsEnabled ? { animationDelay: `${idx * 40}ms` } : {}),
                      borderRadius: rowRadius,
                      ...cardSurface,
                    }}
                    className={`w-full flex items-center justify-between p-3 border border-zinc-100 hover:border-zinc-200/80 transition-all duration-300 ${cardShadowClass} hover:-translate-y-0.5 active:scale-[0.98] cursor-pointer text-left ${animationsEnabled ? "animate-in fade-in slide-in-from-bottom-2" : ""}`}
                  >
                    <div className="flex items-center gap-3.5">
                      <img
                        src={getProductImage(product)}
                        alt={getProductName(product)}
                        loading="lazy"
                        style={{ borderRadius: thumbRadius }}
                        className="size-14 object-cover border border-zinc-100 shadow-sm shrink-0"
                      />
                      <div>
                        <h3
                          className="font-bold text-sm tracking-tight leading-tight"
                          style={{ color: inkColor }}
                        >
                          {getProductName(product)}
                        </h3>
                        <span
                          className="inline-flex items-center gap-1 mt-1.5 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5"
                          style={{
                            color: accentColor,
                            backgroundColor: accentSoft,
                            borderRadius: chipRadius,
                          }}
                        >
                          <Sparkles className="size-2 fill-current" />
                          <span>{product.isFavorite ? t("chefFavorite") : t("popular")}</span>
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {!hidePrices && (
                        <span
                          className="font-extrabold text-zinc-950 text-sm  bg-zinc-50 px-2.5 py-1.5 border border-zinc-100"
                          style={{ borderRadius: thumbRadius }}
                        >
                          {formatTRY(product.price)}
                        </span>
                      )}
                      <ChevronRight className="size-4 text-zinc-300" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* ========================================================
            MAIN SCREEN: CATEGORIES GRID (HOME VIEW)
            ======================================================== */}

        {/* High-End Glassmorphic Cover Banner & Logo Header */}
        <div className="relative w-full h-44 bg-zinc-100 shrink-0 overflow-hidden">
          {/* Banner cover Image */}
          <img
            src={coverSrc}
            alt="Cafe Interior Cover"
            className="w-full h-full object-cover scale-105 hover:scale-100 transition-transform duration-700"
          />
          {/* Dark luxury radial gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-black/30" />

          {/* Glassmorphic Selectbox Language Switcher */}
          {isMultiLang && (
            <div className="absolute top-5 right-5 z-20">
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="backdrop-blur-md bg-white/70 hover:bg-white text-zinc-950 px-4 py-2 rounded-full text-[10px] font-black tracking-widest uppercase shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5 cursor-pointer border border-white/40"
              >
                <Globe className="size-3.5" />
                <span>{lang === "tr" ? "Türkçe" : "English"}</span>
                <ChevronDown
                  className={`size-3 text-zinc-700 transition-transform duration-300 ${isLangDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Selectbox Options Menu */}
              {isLangDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsLangDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-2.5 w-32 bg-white/95 backdrop-blur-md border border-zinc-100 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.12)] py-1.5 z-20 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <button
                      onClick={() => handleSelectLanguage("tr")}
                      className={`w-full text-left px-4 py-2.5 text-xs font-bold flex items-center justify-between hover:bg-zinc-50 transition-colors cursor-pointer ${
                        lang === "tr" ? "text-zinc-950 bg-zinc-50/40" : "text-zinc-400"
                      }`}
                    >
                      <span>Türkçe</span>
                      {lang === "tr" && <Check className="size-3.5 text-zinc-950 stroke-[2.5]" />}
                    </button>
                    <button
                      onClick={() => handleSelectLanguage("en")}
                      className={`w-full text-left px-4 py-2.5 text-xs font-bold flex items-center justify-between hover:bg-zinc-50 transition-colors cursor-pointer ${
                        lang === "en" ? "text-zinc-950 bg-zinc-50/40" : "text-zinc-400"
                      }`}
                    >
                      <span>English</span>
                      {lang === "en" && <Check className="size-3.5 text-zinc-950 stroke-[2.5]" />}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Logo overlapping the cover boundary */}
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
            <div className="size-24 rounded-full bg-white border-[6px] border-white flex items-center justify-center p-4 shadow-[0_12px_32px_rgba(0,0,0,0.12)] transition-transform duration-300 hover:rotate-6">
              <img src={logoSrc} alt={businessName} className="w-full h-full object-contain" />
            </div>
          </div>
        </div>

        {/* Restaurant Branding Section */}
        <header className="pt-14 pb-4 px-6 flex flex-col items-center justify-center shrink-0">
          <h1
            className="text-2xl font-black tracking-[0.2em] uppercase leading-none"
            style={{ color: inkColor }}
          >
            {businessName}
          </h1>
          <span className="w-6 h-0.5 rounded-full mt-2" style={{ backgroundColor: accentColor }} />
          <p
            className="text-[10px] font-extrabold uppercase tracking-widest mt-2"
            style={{ color: accentColor }}
          >
            {t("title")}
          </p>
          <p className="text-[9px] text-zinc-400 mt-1 italic">
            {settings.businessDescription?.trim() || t("subtitle")}
          </p>
        </header>

        {/* Search Bar - Custom Neumorphic Styling */}
        {showSearchBar && (
          <div className="px-6 py-4 shrink-0 border-b border-zinc-100">
            <div className="relative flex items-center">
              <Search className="absolute left-4 size-4.5 text-zinc-450 pointer-events-none" />
              <input
                type="text"
                placeholder={t("searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value.trim() !== "") {
                    setSelectedCategoryId(null);
                  }
                }}
                style={{ borderRadius: Math.max(10, Math.round(radius * 0.6)) }}
                className="w-full py-3.5 pl-11 pr-10 bg-zinc-50 border-none text-sm text-zinc-800 placeholder:text-zinc-400 outline-none focus:bg-white focus:ring-1 focus:ring-zinc-200 transition-all shadow-inner"
              />
            </div>
          </div>
        )}

        {/* Categories / Search Feed Scroll View */}
        <div className="flex-1 overflow-y-auto">
          {searchQuery.trim() !== "" ? (
            /* VIEW A: Search Results Panel */
            <div className="px-6 py-5 animate-in fade-in duration-300">
              <div className="mb-4 text-[10px] font-black uppercase tracking-[0.15em] text-zinc-400">
                {t("searchResults")}
              </div>

              {displayedProducts.length > 0 ? (
                <div className="space-y-3 pb-8">
                  {displayedProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => setSelectedProduct(product)}
                      style={{ borderRadius: rowRadius, ...cardSurface }}
                      className={`w-full flex items-center justify-between p-3 border border-zinc-100 ${cardShadowClass} hover:border-zinc-200/80 active:scale-[0.98] transition-all cursor-pointer text-left`}
                    >
                      <div className="flex items-center gap-3.5">
                        <img
                          src={getProductImage(product)}
                          alt={getProductName(product)}
                          loading="lazy"
                          style={{ borderRadius: thumbRadius }}
                          className="size-12 object-cover border border-zinc-100 shadow-sm shrink-0"
                        />
                        <div>
                          <h3
                            className="font-bold text-sm leading-tight"
                            style={{ color: inkColor }}
                          >
                            {getProductName(product)}
                          </h3>
                          <p className="text-[10px] text-zinc-400 mt-1">{t("choice")}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!hidePrices && (
                          <span
                            className="font-extrabold text-zinc-950 text-sm  bg-zinc-50 px-2.5 py-1"
                            style={{ borderRadius: thumbRadius }}
                          >
                            {formatTRY(product.price)}
                          </span>
                        )}
                        <ChevronRight className="size-4 text-zinc-300" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-12 flex flex-col items-center justify-center text-center text-zinc-400">
                  <p className="text-sm font-bold">{t("noProducts")}</p>
                  <p className="text-xs text-zinc-450 mt-1.5">{t("noProductsSub")}</p>
                </div>
              )}
            </div>
          ) : (
            /* VIEW B: Signature Category Grid Blocks (Masterpiece Neumorphic Cards) */
            <div className="px-6 py-6 space-y-4">
              {/* Promotional Banners Carousel */}
              {activeBanners.length > 0 && (
                <div className="space-y-2.5">
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-400">
                    {t("promotions")}
                  </div>
                  <div
                    className={`flex gap-3 ${activeBanners.length > 1 ? "overflow-x-auto snap-x pb-1 -mx-1 px-1 scrollbar-none" : ""}`}
                  >
                    {activeBanners.map((b) => (
                      <div
                        key={b.id}
                        style={{ borderRadius: radius }}
                        className={`relative shrink-0 h-28 overflow-hidden snap-start ${activeBanners.length > 1 ? "w-[86%]" : "w-full"} ${cardStyle === "elevated" ? "shadow-[0_8px_24px_rgba(0,0,0,0.08)]" : ""}`}
                      >
                        <img
                          src={b.image}
                          alt={b.title}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                        <div className="absolute bottom-0 inset-x-0 p-3.5">
                          <span className="block font-extrabold text-white text-sm leading-tight drop-shadow-sm">
                            {b.title}
                          </span>
                          {b.dateRange && (
                            <span
                              className="mt-1.5 inline-block text-[9px] font-bold text-white bg-white/25 backdrop-blur-sm px-2 py-0.5"
                              style={{ borderRadius: chipRadius }}
                            >
                              {b.dateRange}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-400">
                {t("allCategories")}
              </div>

              <div className="grid grid-cols-2 gap-4 pb-6">
                {/* Favorites Card Block — full photo + gradient */}
                {showFavorites && getFavoritesCount() > 0 && (
                  <button
                    onClick={() => setSelectedCategoryId("favorites")}
                    style={{ borderRadius: radius }}
                    className={`group relative aspect-square overflow-hidden text-left border border-zinc-100 ${cardStyle === "elevated" ? "shadow-[0_12px_32px_rgba(0,0,0,0.08)]" : ""} transition-all duration-500 hover:scale-[1.03] active:scale-[0.97] cursor-pointer`}
                  >
                    <img
                      src={categories.find((c) => c.id === "favorites")?.image}
                      alt={t("favorites")}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                    {showCategoryIcons && (
                      <div className="absolute top-3 left-3 size-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                        <Star
                          className={`size-4 fill-current ${animationsEnabled ? "animate-pulse" : ""}`}
                          style={{ color: accentColor }}
                        />
                      </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 p-4">
                      <h3 className="font-extrabold text-white text-[13px] tracking-tight leading-tight drop-shadow-sm">
                        {t("favorites")}
                      </h3>
                      <div className="flex items-center justify-between mt-2">
                        <span
                          className="text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 text-white"
                          style={{
                            backgroundColor: hexToRgba(accentColor, 0.9),
                            borderRadius: chipRadius,
                          }}
                        >
                          {getFavoritesCount()} {t("items")}
                        </span>
                        <ChevronRight className="size-4 text-white/70" />
                      </div>
                    </div>
                  </button>
                )}

                {/* Normal Category Card Blocks — full photo + gradient */}
                {categories.map((cat) => {
                  const CatIcon = cat.icon;
                  const count = getProductCount(cat.id);
                  if (count === 0) return null;

                  const style = CATEGORY_STYLES[cat.id] || {
                    bg: "bg-zinc-50",
                    text: "text-zinc-700",
                    glow: "",
                    badge: "bg-zinc-100 text-zinc-500",
                  };

                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategoryId(cat.id)}
                      style={{ borderRadius: radius }}
                      className={`group relative aspect-square overflow-hidden text-left border border-zinc-100 ${cardStyle === "elevated" ? "shadow-[0_12px_32px_rgba(0,0,0,0.08)]" : ""} transition-all duration-500 hover:scale-[1.03] active:scale-[0.97] cursor-pointer`}
                    >
                      <img
                        src={cat.image}
                        alt={getCategoryLabel(cat.id, cat.label)}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                      {showCategoryIcons && (
                        <div className="absolute top-3 left-3 size-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                          <CatIcon className={`size-4 ${style.text}`} />
                        </div>
                      )}
                      <div className="absolute bottom-0 inset-x-0 p-4">
                        <h3 className="font-extrabold text-white text-[13px] tracking-tight leading-tight drop-shadow-sm">
                          {getCategoryLabel(cat.id, cat.label)}
                        </h3>
                        <div className="flex items-center justify-between mt-2">
                          <span
                            className="text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 bg-white/25 text-white backdrop-blur-sm"
                            style={{ borderRadius: chipRadius }}
                          >
                            {count} {t("items")}
                          </span>
                          <ChevronRight className="size-4 text-white/70" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Contact & Location Footer */}
              {hasContactInfo && (
                <div className="pt-5 pb-10 border-t border-zinc-100 flex flex-col items-center text-center gap-4">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-400">
                    {t("contactTitle")}
                  </span>

                  {contactChannels.length > 0 && (
                    <div className="flex flex-wrap items-start justify-center gap-x-4 gap-y-3">
                      {contactChannels.map((ch) => {
                        const ChIcon = ch.icon;
                        return (
                          <a
                            key={ch.label}
                            href={ch.href}
                            target="_blank"
                            rel="noreferrer"
                            className="flex flex-col items-center gap-1.5 group w-14"
                          >
                            <span
                              className="size-11 rounded-full flex items-center justify-center shadow-sm transition-all group-hover:scale-105 group-active:scale-95"
                              style={{ backgroundColor: accentSoft, color: accentColor }}
                            >
                              <ChIcon className="size-4.5" />
                            </span>
                            <span className="text-[9px] font-bold text-zinc-500">{ch.label}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}

                  <div className="space-y-1.5 pt-1">
                    {businessAddress && (
                      <p className="text-[11px] text-zinc-500 font-medium leading-relaxed max-w-[280px] flex items-start gap-1.5 justify-center">
                        <MapPin className="size-3 mt-0.5 shrink-0 text-zinc-400" />
                        <span>{businessAddress}</span>
                      </p>
                    )}
                    {workingHours && (
                      <p className="text-[11px] text-zinc-500 font-medium flex items-center gap-1.5 justify-center">
                        <Clock className="size-3 shrink-0 text-zinc-400" />
                        <span>{workingHours}</span>
                      </p>
                    )}
                  </div>

                  <span className="text-[8px] text-zinc-400  font-bold pt-1">
                    Powered by{" "}
                    <strong className="font-black" style={{ color: accentColor }}>
                      Vidyon
                    </strong>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ========================================================
            PRODUCT DETAIL BOTTOM SHEET
            ======================================================== */}
        {selectedProduct && (
          <div className="absolute inset-0 z-50">
            {/* Backdrop */}
            <div
              className={`absolute inset-0 bg-black/30 backdrop-blur-[2px] ${animationsEnabled ? "animate-in fade-in duration-300" : ""}`}
              onClick={() => setSelectedProduct(null)}
            />

            {/* Sheet Panel */}
            <div
              style={{
                borderTopLeftRadius: radius + 4,
                borderTopRightRadius: radius + 4,
                ...(cardStyle === "glass"
                  ? { backgroundColor: "rgba(255,255,255,0.85)", backdropFilter: "blur(16px)" }
                  : { backgroundColor: "#FFFFFF" }),
              }}
              className={`absolute bottom-0 inset-x-0 shadow-[0_-20px_60px_rgba(0,0,0,0.18)] overflow-hidden ${animationsEnabled ? "animate-in slide-in-from-bottom duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" : ""}`}
            >
              {/* Drag handle */}
              <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-white/80 shadow-sm z-10" />

              {/* Close button */}
              <button
                onClick={() => setSelectedProduct(null)}
                aria-label={t("close")}
                className="absolute top-4 right-4 z-10 size-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-zinc-700 hover:text-zinc-950 shadow-md transition-all hover:scale-105 active:scale-95 cursor-pointer"
              >
                <X className="size-4" />
              </button>

              {/* Product Photo Header */}
              <div className="relative h-44 w-full bg-zinc-100">
                <img
                  src={getProductImage(selectedProduct)}
                  alt={getProductName(selectedProduct)}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/10" />
              </div>

              {/* Product Details */}
              <div className="px-6 pt-5 pb-8 space-y-4">
                <div>
                  <h3
                    className="text-lg font-black tracking-tight leading-tight"
                    style={{ color: inkColor }}
                  >
                    {getProductName(selectedProduct)}
                  </h3>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                    <span
                      className={`inline-flex items-center text-[9px] font-extrabold uppercase tracking-widest px-2.5 py-1 ${CATEGORY_STYLES[selectedProduct.categoryId]?.badge || "bg-zinc-100 text-zinc-600"}`}
                      style={{ borderRadius: chipRadius }}
                    >
                      {selectedProductCategoryLabel}
                    </span>
                    {selectedProduct.isFavorite && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-widest px-2.5 py-1 border"
                        style={{
                          color: accentColor,
                          backgroundColor: accentSoft,
                          borderColor: hexToRgba(accentColor, 0.25),
                          borderRadius: chipRadius,
                        }}
                      >
                        <Star className="size-2.5 fill-current" /> {t("chefFavorite")}
                      </span>
                    )}
                    {selectedProduct.isAvailable === false && (
                      <span
                        className="inline-flex items-center text-[9px] font-extrabold uppercase tracking-widest px-2.5 py-1 bg-zinc-100 text-zinc-500"
                        style={{ borderRadius: chipRadius }}
                      >
                        {t("outOfStock")}
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-zinc-500 leading-relaxed">
                  {getProductDescription(selectedProduct)}
                </p>

                {/* Kalori & Alerjen bilgi çipleri */}
                {((showCalories && typeof selectedProduct.calories === "number") ||
                  (showAllergens && getProductAllergens(selectedProduct).length > 0)) && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {showCalories && typeof selectedProduct.calories === "number" && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] font-bold px-2.5 py-1 bg-orange-50 text-orange-600"
                        style={{ borderRadius: chipRadius }}
                      >
                        <Flame className="size-2.5" /> {selectedProduct.calories} kcal
                      </span>
                    )}
                    {showAllergens &&
                      getProductAllergens(selectedProduct).map((a) => (
                        <span
                          key={a}
                          className="inline-flex items-center text-[9px] font-bold px-2.5 py-1 bg-red-50 text-red-600"
                          style={{ borderRadius: chipRadius }}
                        >
                          {a}
                        </span>
                      ))}
                  </div>
                )}

                {!hidePrices && (
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
                    <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400">
                      {t("price")}
                    </span>
                    <span className="font-extrabold text-zinc-950 text-xl ">
                      {formatTRY(selectedProduct.price)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
